import { BadgeSecondarySubtle, Icon } from "@studio3t/generic-components";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { testSuitesApi } from "@/api/client";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";
import { queryKeys } from "@/hooks/queryKeys";
import { useTestSuites } from "@/hooks/useApi";
import type { TestSuite } from "@/types/api";
import { useInlineRename } from "./inlineRename";
import {
  ExplorerDropZone,
  LeadingIcon,
  RemoveRowButton,
  RowActionButton,
  RowActionsGroup,
  toggleActivationProps,
} from "./PipelineNavBar";
import {
  countSavedTests,
  isDefaultUntitledName,
  isUntitledTestId,
  meaningfulAssertions,
  parseSuiteRowId,
  type StagedResource,
  TESTS_EDITOR_ID,
  usePipelineStaging,
} from "./pipelineStaging";
import { useActiveExplorerDrag } from "./StageNavPanel";
import { AddSquareIcon, IndentArrowIcon } from "./sectionIcons";
import {
  AccentBar,
  ItemBody,
  ItemRow,
  PseudoIconWrap,
  PseudoText,
  RowName,
} from "./stageRowChrome";
import { useGenerateSuiteTests } from "./useGenerateSuiteTests";
import { TESTS_SKIP, useRunSkips } from "./useRunSkipsOperations";
import { SUITE_RUN_ID, useRunSuiteTest } from "./useRunSuiteTest";
import { useEnsureStagedSuite, useSuiteCatalog } from "./useTestSuiteSync";

// The Tests tab's structure panel: ONE staged suite group (Figma empty
// 14784-219174, populated 14784-263335, untitled 14790-130430) — the
// suite header row (Tests icon, name, count badge, Run/Plus/Wand/Minus)
// over its indented test rows, in a green-tinted block closed
// by a separator. Saves write through to the suite in the catalog; the
// panel mirrors the suite (old /studio semantics). ONE suite per
// pipeline: the drop invitation only shows on the empty panel, and the
// tree's suite rows drag-lock while a group is staged — swapping suites
// goes unstage-first (or the editor's Add-a-new-test-suite link, which
// confirm-gates unsaved work). The suite Run runs every case as one
// request and the test rows' Runs one each (useRunSuiteTest); the Wand
// generates tests into the suite (useGenerateSuiteTests).
export function TestsNavPanel() {
  const {
    tests,
    stagedSuite,
    unstageSuiteGroup,
    hasDirtyTestWork,
    testEditorId,
    closeTestEditor,
  } = usePipelineStaging();
  const drag = useActiveExplorerDrag();
  const stage = useSuiteDropStaging();
  const remove = useTestDeletion();

  // Unstage flow: dirty group confirms; clean group just goes.
  const [confirmUnstage, setConfirmUnstage] = useState(false);

  // The pseudo-row's highlight (tint + accent + dark text) shows while the
  // panel is at rest empty OR while the pseudo-row holds the editor
  // hand-off after a close/delete/discard — the stage tabs' rules.
  const empty = stagedSuite === null && tests.length === 0;
  const pseudoActive = empty || testEditorId === TESTS_EDITOR_ID;
  const skips = useRunSkips();

  return (
    <Panel data-testid="tests-nav-panel">
      {stagedSuite && (
        <StagedSuiteGroup
          suite={stagedSuite}
          onRequestDelete={remove.request}
          onUnstage={() =>
            hasDirtyTestWork() ? setConfirmUnstage(true) : unstageSuiteGroup()
          }
        />
      )}
      {stagedSuite && skips && (
        <RunNote data-testid="tests-skip-operations-note">
          {TESTS_SKIP[skips]}
        </RunNote>
      )}

      {/* ONE suite per pipeline: the invitation (and its drop zone) only
          exists while nothing is staged — unstaging brings it back. */}
      {stagedSuite === null && (
        <TestsPseudoRow
          dragging={drag?.isTest === true}
          active={pseudoActive}
          onDrop={stage.request}
          // A click selects the pseudo-row like on the stage tabs: the
          // editor shows its links face (where "Add a new test suite"
          // creates a catalog suite) and every kept draft stays.
          onActivate={closeTestEditor}
        />
      )}

      <DirtyGroupModal
        isOpen={confirmUnstage}
        title="Remove test suite"
        confirmLabel="Remove"
        consequence="Remove it from the pipeline anyway? The suite stays in the catalog."
        onConfirm={() => {
          unstageSuiteGroup();
          setConfirmUnstage(false);
        }}
        onCancel={() => setConfirmUnstage(false)}
      />
      <DeleteTestModal
        target={remove.target}
        loading={remove.deleting}
        error={remove.error}
        onConfirm={remove.confirm}
        onCancel={remove.cancel}
      />
    </Panel>
  );
}

// Saved rows imply a stored suite — a PENDING group has only drafts, so a
// null suiteId can't reach this path; a missing target or staged suite
// guards against a stray confirm after cancel/unstage races it. Resolves
// to the values the delete call needs, or null when it isn't safe to run.
function deletableTarget(
  target: { id: string; name: string } | null,
  stagedSuite: { suiteId: string | null; name: string } | null,
): { id: string; name: string; suiteId: string } | null {
  if (!target) return null;
  if (!stagedSuite || stagedSuite.suiteId === null) return null;
  return { id: target.id, name: target.name, suiteId: stagedSuite.suiteId };
}

// A test row's minus: the case leaves the catalog suite, and only a
// successful write unstages the row and closes the confirm — a failed one
// keeps the modal up with its error so the user can retry.
function useTestDeletion() {
  const { stagedSuite, unstage } = usePipelineStaging();
  const catalog = useSuiteCatalog();
  const [target, setTarget] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    const ready = deletableTarget(target, stagedSuite);
    if (!ready) return;
    setDeleting(true);
    setError(null);
    try {
      await catalog.deleteCase(ready.suiteId, ready.name);
      unstage(ready.id);
      setTarget(null);
    } catch {
      setError("Failed to delete test");
    } finally {
      setDeleting(false);
    }
  };

  return {
    target,
    deleting,
    error,
    request: (next: { id: string; name: string }) => {
      setError(null);
      setTarget(next);
    },
    confirm,
    cancel: () => {
      setTarget(null);
      setError(null);
    },
  };
}

// The green-tinted group block: the suite header row over its test rows,
// closed by the separator hairline. The header's inline rename writes
// through to the catalog suite before the staged copy follows it.
function StagedSuiteGroup({
  suite,
  onRequestDelete,
  onUnstage,
}: {
  /** suiteId null = the PENDING group: named but not yet in the catalog. */
  suite: { suiteId: string | null; name: string };
  onRequestDelete: (target: { id: string; name: string }) => void;
  onUnstage: () => void;
}) {
  const { tests, renameStagedSuite, openNewTestDraft, testEditorId } =
    usePipelineStaging();
  // The header icon signals WHERE the selection lives: any selected test
  // row (saved or untitled) makes its group the "clicked" one; the
  // pseudo-row hand-off and the resting state leave it dark.
  const holdsSelection =
    testEditorId !== null && testEditorId !== TESTS_EDITOR_ID;
  const catalog = useSuiteCatalog();
  const { data: suites } = useTestSuites();
  const generation = useGenerateSuiteTests();
  const testRun = useRunSuiteTest();
  const nameTaken = (name: string) =>
    (suites ?? []).some(
      (s) =>
        s.id !== suite.suiteId &&
        s.name.trim().toLowerCase() === name.trim().toLowerCase(),
    );
  // The count badge counts only FINAL tests — saved with content and a
  // proper name (untitled drafts are not tests yet; a saved test is
  // never empty since Save is gated on a meaningful assertion).
  const countedTests = countSavedTests(tests);
  return (
    <SuiteGroup data-testid="tests-suite-group">
      <SuiteRowItem
        name={suite.name}
        count={countedTests}
        active={holdsSelection}
        // Run all needs cases to run; an empty group's Run waits disabled.
        onRun={testRun.runAll}
        runDisabled={testRun.runningId === SUITE_RUN_ID || tests.length === 0}
        onAddTest={openNewTestDraft}
        onGenerate={generation.generate}
        generateDisabled={generation.generating}
        // The staged header follows the catalog only once the write
        // lands; a rejection returns false so the rename field holds the
        // typed name for another attempt. A PENDING group renames
        // locally — there is no catalog row yet.
        onRename={async (name) => {
          if (suite.suiteId !== null) {
            try {
              await catalog.renameSuite(suite.suiteId, name);
            } catch {
              return false;
            }
          }
          renameStagedSuite(name);
          return true;
        }}
        validateRename={(name) =>
          nameTaken(name) ? "A test suite with this name already exists" : null
        }
        onUnstage={onUnstage}
      />
      <SuiteChildRows
        suiteId={suite.suiteId}
        onRequestDelete={onRequestDelete}
      />
      <GroupSeparator />
    </SuiteGroup>
  );
}

// The invitation row on the empty panel — a drop zone while a test drag
// runs, the "Drag a test suite…" pseudo-row otherwise. Rendered only
// while nothing is staged (one suite per pipeline).
function TestsPseudoRow({
  dragging,
  active,
  onDrop,
  onActivate,
}: {
  dragging: boolean;
  active: boolean;
  onDrop: (id: string) => void;
  onActivate: () => void;
}) {
  if (dragging)
    return (
      <ItemRow data-testid="tests-pseudo-row" $active={active} $zone>
        <ExplorerDropZone
          testId="tests-drop-zone"
          shownDuringDrag
          canDrop={(data) => data.isTest === true}
          onDrop={({ id }) => onDrop(id)}
        />
        {active && <AccentBar />}
      </ItemRow>
    );
  return (
    <PseudoGroup $tinted={active}>
      <ItemRow
        data-testid="tests-pseudo-row"
        $active={active}
        $interactive
        {...toggleActivationProps(onActivate)}
      >
        <ItemBody>
          <PseudoIconWrap $emphasis={active}>
            <AddSquareIcon />
          </PseudoIconWrap>
          <PseudoText $emphasis={active}>
            Drag a test suite from the explorer or add one
          </PseudoText>
        </ItemBody>
        {active && <AccentBar />}
      </ItemRow>
    </PseudoGroup>
  );
}

// The gate on a group holding unsaved work — the unstage (and the
// editor's Add-a-new-test-suite link keeps its own copy of it).
function DirtyGroupModal({
  isOpen,
  title,
  confirmLabel,
  consequence,
  onConfirm,
  onCancel,
}: {
  isOpen: boolean;
  title: string;
  confirmLabel: string;
  consequence: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <ConfirmDeleteModal
      isOpen={isOpen}
      title={title}
      confirmLabel={confirmLabel}
      message={
        <>
          The staged suite has unsaved changes that will be lost. {consequence}
        </>
      }
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}

// A test row's minus — the case leaves the catalog suite for good.
function DeleteTestModal({
  target,
  loading,
  error,
  onConfirm,
  onCancel,
}: {
  target: { id: string; name: string } | null;
  loading: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <ConfirmDeleteModal
      isOpen={target !== null}
      loading={loading}
      error={error}
      title="Delete test"
      message={
        <>
          Are you sure you want to delete <strong>{target?.name}</strong>? This
          removes it from the suite and cannot be undone.
        </>
      }
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}

// The drop staging: only the EMPTY panel offers the drop zone (one suite
// per pipeline), so a drop is a plain stage. The cases come from the
// query cache synchronously when the hover preview already loaded the
// suite, from the network otherwise.
export function useSuiteDropStaging(onError?: () => void) {
  const { stagedSuite, stageSuiteGroup } = usePipelineStaging();
  const queryClient = useQueryClient();

  const stageById = (suiteId: string) => {
    const apply = (suite: TestSuite | undefined) => {
      if (suite) stageSuiteGroup(suite, Icon.Test);
    };
    const cached = queryClient.getQueryData<TestSuite>(
      queryKeys.testSuite(suiteId),
    );
    if (cached) apply(cached);
    else
      testSuitesApi
        .get(suiteId)
        .then(apply)
        // A failed fallback fetch means nothing to stage — the drop is a
        // no-op like the other guard paths; a caller with somewhere to
        // report it (the AI Helper's row) hears about it.
        .catch(() => onError?.());
  };

  return {
    request: (id: string) => {
      const suiteId = parseSuiteRowId(id);
      // The drop zone only exists on the empty panel, so this is a plain
      // stage — the staged-suite guard is belt and braces.
      if (!suiteId || stagedSuite !== null) return;
      stageById(suiteId);
    },
  };
}

// The group's children: the suite's saved tests plus any untitled drafts,
// in creation order. Every Save writes the case through to the catalog
// suite the group mirrors; a minus asks the panel for the confirm.
function SuiteChildRows({
  suiteId,
  onRequestDelete,
}: {
  /** null while the group is PENDING — saved-row ops are unreachable
   *  then (only drafts exist); the first draft save creates the stored
   *  suite via ensureSuite. */
  suiteId: string | null;
  onRequestDelete: (target: { id: string; name: string }) => void;
}) {
  const {
    tests,
    testEditorId,
    isTestDirty,
    testDrafts,
    strictModeOf,
    openTestInEditor,
    openTestViewer,
    discardUntitledTest,
    saveNewTest,
    saveTestEdits,
    discardTestEdits,
    renameTest,
    freshTestDraftId,
    clearFreshTestDraft,
    setActiveSaveHandler,
  } = usePipelineStaging();
  const catalog = useSuiteCatalog();
  const ensureSuite = useEnsureStagedSuite();
  const testRun = useRunSuiteTest();

  // A plain selection views a clean row's saved assertions read-only
  // (Figma 14785-268156); a dirty row's kept working copy is a live edit
  // session, so selecting it resumes the editor. The pencil always edits.
  const selectTest = (id: string) =>
    isTestDirty(id) ? openTestInEditor(id) : openTestViewer(id);
  // Test rows only SELECT — clicks never deselect (not the active row's
  // second click, not a click outside the list): a selected test stays
  // until another row takes over or a delete/discard hands off.
  const toggleTest = (id: string, open: () => void) => {
    if (testEditorId !== id) open();
  };
  const testNameTaken = (name: string, ownId: string) =>
    tests.some(
      (t) => !isUntitledTestId(t.id) && t.id !== ownId && t.name === name,
    );
  const takenMessage = (name: string, ownId: string) =>
    testNameTaken(name, ownId) ? "A test with this name already exists" : null;
  // An EMPTY test never saves: a working copy without one meaningful
  // assertion keeps its Save disabled (and is not counted by the badge).
  const emptyDraft = (id: string) =>
    meaningfulAssertions(testDrafts[id] ?? []).length === 0;
  // Saves an untitled draft under the row's name — no modal: the write
  // lands in the catalog (a PENDING group's first save creates the
  // stored suite here) before the row swaps to a saved test. The
  // resolved message shows red on the row's name input.
  // A dirty saved test's row save — shared by the row button and ⌘S.
  // Both writes land in the catalog BEFORE the local state follows: a
  // rejected save keeps the row dirty (and its Save).
  const saveDirtyTest = async (test: { id: string; name: string }) => {
    const rows = testDrafts[test.id];
    if (!rows || suiteId === null) return;
    try {
      await catalog.saveCase(suiteId, test.name, rows, strictModeOf(test.id));
    } catch {
      return;
    }
    saveTestEdits(test.id);
  };

  // ⌘S while the editor holds a dirty SAVED test (untitled drafts need a
  // name first — they fall through to the draft-save warning).
  const editedTest = tests.find((t) => t.id === testEditorId);
  useEffect(() => {
    if (!editedTest || !isTestDirty(editedTest.id)) return;
    if (emptyDraft(editedTest.id)) return;
    setActiveSaveHandler(() => void saveDirtyTest(editedTest));
    return () => setActiveSaveHandler(null);
  });

  const saveUntitled = async (id: string, name: string) => {
    if (testNameTaken(name, id)) return "A test with this name already exists";
    try {
      const sid = await ensureSuite();
      await catalog.saveCase(sid, name, testDrafts[id] ?? [], strictModeOf(id));
    } catch {
      return "Failed to save test";
    }
    saveNewTest(id, name, Icon.Test);
    return null;
  };

  return (
    <>
      {/* The EMPTY group's standing invitation (Figma 15013-210630): a
          selected-looking "Untitled test*" row with no actions — the
          first test that hasn't been started yet. The editor's "Write a
          test" link mints the real draft in its place. */}
      {tests.length === 0 && (
        <ItemRow data-testid="tests-untitled-invitation" $active>
          <ItemBody>
            <IndentIconWrap $active>
              <IndentArrowIcon />
            </IndentIconWrap>
            <RowName>Untitled test*</RowName>
          </ItemBody>
          <AccentBar />
        </ItemRow>
      )}
      {tests.map((test) =>
        isUntitledTestId(test.id) ? (
          <UntitledTestRow
            key={test.id}
            name={test.name}
            editing={testEditorId === test.id}
            // The just-minted row opens with its name input active (the
            // "Write a test" / Plus first-naming affordance).
            startNaming={freshTestDraftId === test.id}
            onStartNamingConsumed={clearFreshTestDraft}
            onToggle={() =>
              toggleTest(test.id, () => openTestInEditor(test.id))
            }
            onRun={() => testRun.run(test)}
            runDisabled={testRun.runningId === test.id}
            saveDisabled={emptyDraft(test.id)}
            onSave={(name) => saveUntitled(test.id, name)}
            // A draft rename is client-side only — nothing in the
            // catalog to write until Save.
            onRename={async (name) => {
              renameTest(test.id, name);
              return true;
            }}
            validateRename={(name) => takenMessage(name, test.id)}
            onDiscard={() => discardUntitledTest(test.id)}
          />
        ) : (
          <TestRowItem
            key={test.id}
            test={test}
            editing={testEditorId === test.id}
            dirty={isTestDirty(test.id)}
            onRevert={() => discardTestEdits(test.id)}
            onRun={() => testRun.run(test)}
            runDisabled={testRun.runningId === test.id}
            saveDisabled={emptyDraft(test.id)}
            // The pencil always (re)opens the editor.
            onToggle={() => toggleTest(test.id, () => selectTest(test.id))}
            onSelect={() => selectTest(test.id)}
            onOpen={() => openTestInEditor(test.id)}
            // Both writes land in the catalog BEFORE the local state
            // follows: a rejected save keeps the row dirty (and its
            // Save), a rejected rename returns false so the rename field
            // holds the typed name for another attempt.
            onSave={() => void saveDirtyTest(test)}
            onRename={async (name) => {
              if (suiteId === null) return false;
              try {
                await catalog.renameCase(suiteId, test.name, name);
              } catch {
                return false;
              }
              renameTest(test.id, name);
              return true;
            }}
            validateRename={(name) => takenMessage(name, test.id)}
            onRemove={() => onRequestDelete({ id: test.id, name: test.name })}
          />
        ),
      )}
    </>
  );
}

// The group's header row (Figma 14784-263335): Tests icon, the suite
// name (double-click renames inline, writing through to the catalog),
// the case count badge and the Run/Plus/Wand/Minus actions. No chevron —
// the group never collapses, so expanding is not a control. The row is
// NOT clickable/selectable (only the child rows carry selection), so it
// carries no hover tint either (TTTL-214) — its buttons hover on their
// own.
function SuiteRowItem({
  name,
  count,
  active,
  onRun,
  runDisabled,
  onAddTest,
  onGenerate,
  generateDisabled,
  onRename,
  validateRename,
  onUnstage,
}: {
  name: string;
  count: number;
  /** True while a test inside the group holds the selection — the header
   *  icon goes mainprimary (Figma 15013-205248 vs 15013-204539: the
   *  clicked vs unclicked group). */
  active: boolean;
  /** Runs every case of the group as one request. */
  onRun: () => void;
  runDisabled: boolean;
  onAddTest: () => void;
  onGenerate: () => void;
  generateDisabled: boolean;
  onRename: (name: string) => Promise<boolean>;
  validateRename: (name: string) => string | null;
  onUnstage: () => void;
}) {
  const rename = useInlineRename({
    name,
    dirty: false,
    testId: "suite-rename-input",
    onRename,
    validateRename,
    ensureSelected: () => {},
    // The count badge hugs the name instead of drifting to the row's edge.
    fit: true,
  });
  return (
    <ItemRow data-testid="tests-suite-row">
      <ItemBody>
        <LeadingIcon icon={Icon.Tests} active={active} />
        {rename.nameNode}
        <BadgeSecondarySubtle>{count}</BadgeSecondarySubtle>
      </ItemBody>
      <RowActionsGroup>
        <RowActionButton
          label={`Run ${name}`}
          icon={Icon.Run}
          viewSafe
          onClick={onRun}
          disabled={runDisabled}
        />
        <RowActionButton
          label={`Add a test to ${name}`}
          icon={Icon.Plus}
          onClick={onAddTest}
        />
        <RowActionButton
          label={`Generate a test in ${name}`}
          icon={Icon.QueryBuilder}
          onClick={onGenerate}
          disabled={generateDisabled}
        />
        <RemoveRowButton name={name} onRemove={onUnstage} />
      </RowActionsGroup>
    </ItemRow>
  );
}

// A saved test inside the group (Figma 14784-263335): the ↳ indent glyph,
// the name (double-click renames inline, starred while its edits are
// unsaved) and run + edit-or-save + remove — a dirty row keeps Run first,
// then the revert action (TTTL-214: Play, Back, Save) discarding the
// unsaved edits back to the saved lines.
function TestRowItem({
  test,
  editing,
  dirty,
  onToggle,
  onRevert,
  onRun,
  runDisabled,
  saveDisabled,
  onSelect,
  onOpen,
  onSave,
  onRename,
  validateRename,
  onRemove,
}: {
  test: StagedResource;
  editing: boolean;
  dirty: boolean;
  onToggle: () => void;
  /** Returns a dirty row's unsaved changes to the saved lines — the
   *  revert action leading the row while it is dirty. */
  onRevert: () => void;
  /** Runs this single case; disabled only while ITS run is in flight. */
  onRun: () => void;
  runDisabled: boolean;
  /** True while the working copy holds no meaningful assertion — an
   *  empty test never saves. */
  saveDisabled: boolean;
  /** Selects without toggling (viewer or editor by dirtiness). */
  onSelect: () => void;
  onOpen: () => void;
  onSave: () => void;
  onRename: (name: string) => Promise<boolean>;
  validateRename: (name: string) => string | null;
  onRemove: () => void;
}) {
  const rename = useInlineRename({
    name: test.name,
    dirty,
    testId: "test-rename-input",
    onRename,
    validateRename,
    // Switching rows closes an open name input (re-open via
    // double-click).
    selected: editing,
    ensureSelected: () => {
      if (!editing) onSelect();
    },
  });
  return (
    <ItemRow
      data-testid="pipeline-tests-item"
      data-resource-id={test.id}
      $active={editing}
      $interactive
      {...toggleActivationProps(onToggle)}
    >
      <ItemBody>
        <IndentIconWrap $active={editing}>
          <IndentArrowIcon />
        </IndentIconWrap>
        {rename.nameNode}
      </ItemBody>
      <RowActionsGroup>
        <RowActionButton
          label={`Run ${test.name}`}
          icon={Icon.Run}
          viewSafe
          onClick={onRun}
          disabled={runDisabled}
        />
        {dirty && (
          <RowActionButton
            label={`Revert changes to ${test.name}`}
            icon={Icon.Undo}
            onClick={onRevert}
          />
        )}
        {dirty ? (
          <RowActionButton
            label={`Save ${test.name}`}
            icon={Icon.Save}
            onClick={onSave}
            disabled={saveDisabled}
          />
        ) : (
          <RowActionButton
            label={`Edit ${test.name}`}
            icon={Icon.Edit}
            onClick={onOpen}
          />
        )}
        <RemoveRowButton name={test.name} onRemove={onRemove} />
      </RowActionsGroup>
      {editing && <AccentBar />}
    </ItemRow>
  );
}

// The freshly minted test: an "Untitled test*" label like the transform
// tab's Untitled script row — Save writes the case straight to the suite
// under the row's CURRENT name (the open input's text, or the committed
// name; the default "Untitled test N" is valid too). A duplicate name or
// a failed write paints the name input red with the message instead of
// saving. The minus drops the draft with the row; the name double-click
// renames the draft locally.
function UntitledTestRow({
  name,
  editing,
  startNaming,
  onStartNamingConsumed,
  onToggle,
  onRun,
  runDisabled,
  saveDisabled,
  onSave,
  onRename,
  validateRename,
  onDiscard,
}: {
  name: string;
  editing: boolean;
  /** Mounts with the name input already open (a just-minted draft). */
  startNaming: boolean;
  onStartNamingConsumed: () => void;
  onToggle: () => void;
  /** Runs the draft's single case; disabled only while ITS run flies. */
  onRun: () => void;
  runDisabled: boolean;
  /** True while the draft holds no meaningful assertion — an empty test
   *  never saves. */
  saveDisabled: boolean;
  /** Saves the draft under this name; resolves an error message to show
   *  on the name input, or null on success. */
  onSave: (name: string) => Promise<string | null>;
  /** Renames the draft locally (client-side only). */
  onRename: (name: string) => Promise<boolean>;
  validateRename: (name: string) => string | null;
  onDiscard: () => void;
}) {
  const [saving, setSaving] = useState(false);
  // The star marks the un-named default only — a custom-named draft
  // reads clean (Save still handles persisting).
  const unnamed = isDefaultUntitledName(name, "Untitled test");
  const rename = useInlineRename({
    name,
    dirty: true,
    star: unnamed,
    testId: "untitled-test-rename-input",
    onRename,
    validateRename,
    // Switching rows closes the naming input; while the row is still
    // un-named, coming back re-opens it (the standing affordance).
    selected: editing,
    reopenOnSelect: unnamed,
    // The double-click's first click selects the row (onToggle is
    // select-only, so an already-selected row stays put).
    ensureSelected: () => {
      if (!editing) onToggle();
    },
    // The input opens visibly but hands-off: focus belongs to the
    // assertion editor's first field, so typing starts the test — the
    // same rule as the transform tab's script mint.
    startEditing: startNaming,
  });
  // Consume the fresh-draft flag once the open input captured it, so a
  // later remount (tab switch) shows the plain label again.
  useEffect(() => {
    if (startNaming) onStartNamingConsumed();
  }, [startNaming, onStartNamingConsumed]);
  // Direct save under the row's pending name; a rejection (duplicate or
  // failed write) opens the name input red with the message.
  const save = async () => {
    if (saving) return;
    setSaving(true);
    const message = await onSave(rename.pendingName);
    setSaving(false);
    if (message) rename.openWithError(message);
  };
  return (
    <ItemRow
      data-testid="tests-untitled-row"
      $active={editing}
      $interactive
      {...toggleActivationProps(onToggle)}
    >
      <ItemBody>
        <IndentIconWrap $active={editing}>
          <IndentArrowIcon />
        </IndentIconWrap>
        {rename.nameNode}
      </ItemBody>
      <RowActionsGroup>
        <RowActionButton
          label={`Run ${name}`}
          icon={Icon.Run}
          viewSafe
          onClick={onRun}
          disabled={runDisabled}
        />
        <RowActionButton
          label="Save test"
          icon={Icon.Save}
          onClick={save}
          disabled={saveDisabled || rename.saveDisabled || saving}
        />
        <RemoveRowButton name={name} onRemove={onDiscard} />
      </RowActionsGroup>
      {editing && <AccentBar />}
    </ItemRow>
  );
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
`;

// What a test run leaves out, under the suite it applies to.
const RunNote = styled.p`
  margin: 0;
  padding: 6px 10px;
  font-size: 12px;
  line-height: 1.4;
  color: ${(p) => p.theme.semanticTextPlaceholder};
`;

const GREEN_BACKGROUND = "rgba(26, 198, 115, 0.05)";

const SuiteGroup = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  background: ${GREEN_BACKGROUND};
`;

// Bridge_PipelineNav/separator — the hairline closing a group block.
const GroupSeparator = styled.div`
  height: 1px;
  flex-shrink: 0;
  background: ${(p) => p.theme.semanticBorderLight};
`;

const IndentIconWrap = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: ${(p) =>
    p.$active ? p.theme.semanticMainPrimary : p.theme.semanticTextHeading};
`;

const PseudoGroup = styled.div<{ $tinted?: boolean }>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  background: ${(p) => (p.$tinted ? GREEN_BACKGROUND : "transparent")};
`;
